import { useEffect, useRef, useState } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { C } from '../MapSharedData';

interface UseUnifiedMapOptions {
  containerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Single MapLibre instance shared between the world overview and country
 * drill-downs (REFACTOR_PLAN.md). Created ONCE with world defaults — never
 * re-created when the active country changes. Drill-in adjusts viewport /
 * minZoom via mapRef directly (added in steps 3+), so layer crossfades can
 * stay on the GPU instead of being broken up by unmount/remount.
 *
 * Style + controls match the legacy useMapLibre / WorldView setup so the
 * visual baseline is identical when USE_UNIFIED is toggled.
 */
export function useUnifiedMap({ containerRef }: UseUnifiedMapOptions) {
  const mapRef = useRef<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new MapLibreMap({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap, © CARTO',
          },
        },
        layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': C.surface } },
          {
            id: 'osm',
            type: 'raster',
            source: 'osm',
            paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
          },
        ],
      },
      center: [10, 25],
      zoom: 1.5,
      attributionControl: { compact: true },
      minZoom: 1,
      maxZoom: 14,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.on('load', () => setReady(true));
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mapRef, ready };
}
